<template>
  <div class="toast-container" :class="{ 'toast-container--opaque': opaque }">
    <transition-group
      v-for="position in Object.keys(toastsByPosition)"
      :key="position"
      name="toast-transition"
      tag="div"
      :class="`toast-group toast-group--${position}`"
    >
      <div
        v-for="toast in toastsByPosition[position]"
        :key="toast.id"
        :class="['toast', `toast--${toast.type}`, `toast--${position}`]"
        @click="removeToast(toast.id)"
      >
        <div class="toast__content">
          <div class="toast__icon">
            <FlFilledCheckmark v-if="toast.type === 'success'" class="toast__icon-success" />
            <FlErrorCircle v-else-if="toast.type === 'error'" class="toast__icon-error" />
            <FlInfo v-else class="toast__icon-info" />
          </div>
          <div class="toast__message">{{ toast.message }}</div>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
import { FlFilledCheckmark, FlInfo, FlErrorCircle } from '@kalimahapps/vue-icons';

export default {
  name: 'ToastContainer',
  components: {
    FlFilledCheckmark,
    FlInfo,
    FlErrorCircle,
  },
  data() {
    return {
      toasts: [],
      toastTimeouts: {},
      opaque: false,
    };
  },
  computed: {
    toastsByPosition() {
      const grouped = {
        'top-right': [],
        'top-left': [],
        'bottom-right': [],
        'bottom-left': [],
      };

      this.toasts.forEach((toast) => {
        if (grouped[toast.position]) {
          grouped[toast.position].push(toast);
        } else {
          grouped['top-right'].push(toast);
        }
      });

      Object.keys(grouped).forEach((position) => {
        if (position.startsWith('top')) {
          grouped[position].sort((a, b) => a.timestamp - b.timestamp);
        } else {
          grouped[position].sort((a, b) => b.timestamp - a.timestamp);
        }
      });

      return grouped;
    },
  },
  methods: {
    addToast(config) {
      this.toasts.push(config);
      if (config.timeout > 0) {
        this.toastTimeouts[config.id] = setTimeout(() => {
          this.removeToast(config.id);
        }, config.timeout);
      }
    },
    removeToast(id) {
      const index = this.toasts.findIndex((toast) => toast.id === id);
      if (index !== -1) {
        if (this.toastTimeouts[id]) {
          clearTimeout(this.toastTimeouts[id]);
          delete this.toastTimeouts[id];
        }
        this.toasts.splice(index, 1);
      }
    },
    clearAll() {
      Object.keys(this.toastTimeouts).forEach((id) => {
        clearTimeout(this.toastTimeouts[id]);
      });
      this.toasts = [];
      this.toastTimeouts = {};
    },
    setOpaque(isOpaque) {
      this.opaque = !!isOpaque;
    },
  },
};
</script>

<style lang="sass">
@use '@/assets/styles/variables.sass' as variables

$toast-success-bg: rgba(#66bb6a, 0.15)
$toast-error-bg: rgba(211, 47, 47, 0.15)
$toast-info-bg: var(--noc-blue-15)

.toast-container
  position: fixed
  z-index: 9999
  pointer-events: none
  width: 100%
  height: 100%
  top: 0
  left: 0
  overflow: hidden

.toast-group
  position: fixed
  display: flex
  flex-direction: column
  align-items: flex-start
  pointer-events: none

  &--top-right
    top: variables.$spacing-medium
    right: variables.$spacing-medium
    align-items: flex-end

  &--top-left
    top: variables.$spacing-medium
    left: variables.$spacing-medium
    align-items: flex-start

  &--bottom-right
    bottom: variables.$spacing-medium
    right: variables.$spacing-medium
    align-items: flex-end
    flex-direction: column-reverse

  &--bottom-left
    bottom: variables.$spacing-medium
    left: variables.$spacing-medium
    align-items: flex-start
    flex-direction: column-reverse

.toast
  position: relative
  max-width: 350px
  min-width: 280px
  padding: variables.$spacing-small variables.$spacing-medium
  background: $toast-info-bg
  color: var(--text-color)
  margin-bottom: 8px
  border-radius: variables.$border-radius
  box-shadow: var(--box-shadow)
  pointer-events: auto
  cursor: pointer
  transition: all 0.3s ease
  backdrop-filter: blur(5px)

  &--success
    background: $toast-success-bg
    color: var(--text-color)

  &--error
    background: $toast-error-bg
    color: var(--text-color)

.toast__content
  display: flex
  align-items: center
  gap: variables.$spacing-small

.toast__icon-success
  color: #2e7d32

.toast__icon-error
  color: #c62828

.toast__icon-info
  color: var(--noc-blue)

.toast-transition-enter-active,
.toast-transition-leave-active
  transition: all 0.2s ease

.toast-transition-enter-from,
.toast-transition-leave-to
  opacity: 0
  transform: translateY(-10px)
</style>
